// client/src/components/layout/bottom-tabs.tsx
//
// Mobile bottom nav — unified with the desktop sidebar. Both surfaces now build
// their entries from the SAME source (nav-items.ts), so they can never again
// offer different options (the bug that prompted this: mobile used to hardcode
// just "Security" + Profile regardless of context, while the desktop sidebar
// showed the full context-aware list).
//
// Universal parity rule: every item the sidebar renders is shown here too, as
// a DIRECT icon tab — never demoted into the "More" overflow sheet. An earlier
// version capped direct tabs at 5 and pushed anything past that into More,
// which silently broke parity for the org context's 6th item (Activity).
// "More" now carries ONLY the profile block (name/AAL, org switcher, Sign
// Out), which has no sidebar equivalent to stay parallel with. See
// bottom-tabs.test.tsx, which asserts item-for-item parity per context so this
// can't silently regress.
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { AnimatePresence } from "framer-motion";
import { Menu } from "lucide-react";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/haptics";
import { useOrgContext } from "@/lib/use-org-context";
import { useCurrentUser } from "@/lib/use-current-user";
import { protectionLevel } from "@/lib/terminology";
import { isPlatformRoute } from "@/lib/route-context";
import { NavBadge } from "./nav-badge";
import { NavLockGlyph } from "./nav-lock-glyph";
import { buildNavItems, buildPlatformNavItems, isBelowMinAal, isNavItemActive, useVisibleNavItems } from "./nav-items";
import { MoreSheet } from "./more-sheet";

interface BottomTabsProps {
  displayName?: string;
  aal?: "AAL1" | "AAL2" | "AAL3";
}

export function BottomTabs({ displayName = "User", aal = "AAL1" }: BottomTabsProps) {
  const [location] = useLocation();
  const { currentContext } = useOrgContext();
  const { user } = useCurrentUser();
  const [showMore, setShowMore] = useState(false);

  const onPlatform = isPlatformRoute(location);
  // Every context item renders directly — no overflow slicing. "More" is
  // always present too, but carries only the profile block (see header note).
  // `role` is read here — the only place these plain builder functions can
  // reach a hook — and threaded into buildPlatformNavItems so it can tighten
  // its gate to operators only. Which profile is active (Personal/Org vs
  // Platform Console) is decided ENTIRELY by `onPlatform` — a platform-admin
  // role never adds a Platform Console tab to the Personal/Org list, and a
  // non-operator role never sees platform tabs while on /admin/platform*.
  const items = useVisibleNavItems(
    onPlatform ? buildPlatformNavItems(user?.role) : buildNavItems(currentContext),
  );

  const tabClass = (active: boolean) =>
    cn(
      "flex flex-col items-center justify-center gap-0.5",
      "flex-1 min-w-0 min-h-[44px] px-1 py-1",
      "transition-colors duration-[var(--duration-fast)]",
      // Emerald active state — matches the desktop sidebar's emerald-forward
      // brand direction (owner-approved 2026-07-16).
      active ? "text-strenn-emerald" : "text-text-tertiary",
    );

  return (
    <>
      <nav
        role="navigation"
        className={cn(
          "fixed bottom-0 left-0 right-0 z-40",
          "h-[var(--bottom-tabs-h)]",
          "pb-[env(safe-area-inset-bottom)]",
          "bg-bg-elevated/85 backdrop-blur-md supports-[not(backdrop-filter:blur(0))]:bg-bg-elevated",
          "border-t border-border-subtle",
          "flex items-center justify-around",
        )}
      >
        {items.map((item) => {
          const active = isNavItemActive(item, location);
          // Lock affordance (AC-7, #1791) — display-only; the route's own
          // Guarded wrapper stays the sole enforcement. Never a raw "AAL3"
          // string (plain-language rule) — protectionLevel() supplies the
          // plain-English requirement.
          const locked = isBelowMinAal(aal, item.minAal);
          // Same fold-in as the sidebar (#2171): this button's aria-label
          // replaces its subtree, so the badge's own label would be silent.
          const badgeCount = item.badgeCount ?? 0;
          // Bar wording, not sidebar wording (Ruling V): the platform context
          // carries six tabs, which only fit a 320px phone at ~53px each with
          // short labels. Items with no `barLabel` are identical on both
          // surfaces. Parity is asserted on HREFS, not strings.
          const barLabel = item.barLabel ?? item.label;
          const label =
            (locked ? `${barLabel} — needs ${protectionLevel(item.minAal!).label}` : barLabel) +
            (badgeCount > 0 ? ` — ${badgeCount} pending` : "");
          return (
            <Link key={item.label} href={item.href}>
              <button
                onClick={() => haptic("light")}
                className={tabClass(active)}
                aria-label={label}
                aria-current={active ? "page" : undefined}
              >
                <span className="relative">
                  <item.icon size={22} />
                  <NavBadge
                    count={badgeCount}
                    label={barLabel}
                    // Same offset the collapsed sidebar uses — one number, so
                    // the two navs cannot drift. -right-2 keeps the chip
                    // inside its own tab at 320px, where 7 buttons leave only
                    // ~46px per tab (DESIGN-SYSTEM.md §4.1).
                    className="absolute -right-2 -top-1"
                  />
                  {/* MAJOR 3 (adversarial review): the requirement text lived
                      only in the button's aria-label — screen-reader-only.
                      NavLockGlyph (Reuse-First; shared with sidebar.tsx so the
                      two navs cannot drift on size/offset) reuses the
                      canonical §3.10 tooltip, consistent with every other
                      MetricTooltip call site in the product. No functional
                      loss either way — the requirement text is already in
                      aria-label — but honest scope note (round-2 adversarial
                      review): this 11px glyph is the only long-press target,
                      nested inside a <button> inside a <Link>, so on a real
                      touch device the outer tap targets are very likely to
                      win the gesture race before Radix's long-press timer
                      fires; the tooltip's DESIGN-SYSTEM-compliant fallback
                      here is realistically desktop-hover, not "both" as the
                      component doc implies for a well-isolated trigger.
                      Enlarging the hit area is a separate design change, not
                      done here. */}
                  {locked && <NavLockGlyph label={label} variant="overlay" />}
                </span>
                {active && (
                  <span className="text-[11px] font-medium truncate max-w-full">{barLabel}</span>
                )}
              </button>
            </Link>
          );
        })}

        {/* More tab — profile block only (name/AAL, org switcher, Sign Out).
            Never represents an active route, so it carries no active state. */}
        <button
          onClick={() => { haptic("light"); setShowMore(true); }}
          className={tabClass(false)}
          aria-label="More"
          aria-haspopup="dialog"
          aria-expanded={showMore}
        >
          <Menu size={22} />
        </button>
      </nav>

      <AnimatePresence>
        {showMore && (
          <MoreSheet
            displayName={displayName}
            aal={aal}
            onClose={() => setShowMore(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
